import * as Enumerable from "linq";
import { ChartLegendViewModel } from "./chart-legend-view-model";
import { ConditionalDecimalPlacesValueConverter } from "../converters/conditional-decimal-places-value-converter";

export interface ChartSettings{
    maxValue: number;
    computedMaxValue: number;
    step: number;
    numberOfSteps: number;
    units: string;
}

export interface ChartItem {
    id: number;
    itemName: string;
    additionalName?: string;
    bars: ChartBar [];
}

export interface ChartBar {
    id: number
    barName: string;
    value: number;
    size: number;
    color?: string;
}

export class BarChartViewModel {
    description: string;
    chartSettings: ChartSettings;
    chartItems: ChartItem[];
    chartLegend: ChartLegendViewModel;
    conditionalDecimalPlacesValueConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();
        
    constructor(chartItems: ChartItem[], maxValue: number,  numberOfSteps: number = 4, units: string = "") {
        this.chartItems = chartItems;
        this.generateChartSettings(maxValue, numberOfSteps, units);
        this.chartLegend = new ChartLegendViewModel();
    }

    generateChartSettings(maxValue: number, numberOfSteps: number, units: string) {
        const settings: ChartSettings = {
            maxValue: 0,
            computedMaxValue: 0,
            step: 0,
            numberOfSteps: numberOfSteps,
            units: units
        };
         
        //Always round to the nearest 100
        const roundNearestValue = maxValue < 100 ? 10 : 100;
        const remainderOfMaxValue = maxValue % roundNearestValue;
        const roundUpOrDown = (roundNearestValue == 10 && remainderOfMaxValue < 5) ||  (roundNearestValue == 100 && maxValue % roundNearestValue < 50) ?
            (maxValue + (roundNearestValue == 100 ? 99 : 9)) : maxValue
        const computedMaxValue = maxValue % 10 !== 0 ?  Math.round(roundUpOrDown / roundNearestValue) * roundNearestValue : maxValue;

        const step =  computedMaxValue / numberOfSteps ;

        let roundedStep = (step % 10) !== 0 ?  Math.round(step / roundNearestValue) * roundNearestValue : step;
        
        //if max value is <= 10 client wants in that case have y axis split into 1, 2, 3... 10 otherwise to avoid uneven numbers like 36 etc we round step to 50
        if(roundedStep == 0 ) roundedStep = maxValue <= 10 ? 1 : 50;
        
        settings.step = roundedStep;
        settings.numberOfSteps = Math.ceil(computedMaxValue / roundedStep);
        settings.maxValue = maxValue;
        settings.computedMaxValue = roundedStep * settings.numberOfSteps;
        this.chartSettings = settings;
    }

    getBarSize(value:number, maxValue:number, containerSize:number) {
        let result = 0;

        if (value > maxValue) {
            return maxValue;
        }
        
        const valuePercentage = value / maxValue;
        result = containerSize * valuePercentage;
        
        if (result > 0 && result < 1)
            return 2; // minimum to make the bar visible
        
        return result;
    }

    getTotalValueForChartItem(itemId: number) {
        return Enumerable.from(this.chartItems).where(x => x.id == itemId).select(x => Enumerable.from(x.bars).sum(x => x.value)).firstOrDefault();
    }
    
    getBarColor(legendId: number) {
        return this.chartLegend.getColor(legendId);
    }
}
